import { gql } from '@apollo/client';

// --- Fragments ---

export const DEVICE_LIST_FIELDS = gql`
  fragment DeviceListFields on Device {
    _id
    UUID
    deviceName
    accountId
    teamId
    path
    lastHeartBeat
    serverTime
    deviceTime
    status
    os
    osVersion
    device
    model
    manufacturer
    platform
    serialNo
    typeBuild
    currentType
    currentAssetId
    currentPlaylistId
    currentScheduleId
    currentSelectionDate
    scale
    orientation
    tags
    muted
    activationDate
    creationDate
    functionalLocationId
    location {
      coordinates
      name
    }
    preloadId
    feature
    temporarily
    scheduleGoLive
    statusContent
    original
    flashMessage
    recentAssignments
    assignItem {
      key
      value
      type
      teamId
      isAccessible
    }
    isContentExpired
    fleetId
    classification {
      platform
      vendor
      hardwareFamily
      managementCapability
      osVersionDisplay
      osVersionMajor
      modelCode
      manufacturerRaw
      platformRaw
    }
    availableCommands
    reportedCapabilities
  }
`;

export const DEVICE_DETAIL_FIELDS = gql`
  fragment DeviceDetailFields on Device {
    ...DeviceListFields
    osVersion
    manufacturer
    localAppVersion
    serialNo
    isVirtual
    isWebViewer
    stretchAsset
    documentDuration
    pollingInterval
    heartbeatInterval
    videoPlayerType
    backgroundType
    backgroundAssetId
    backgroundAWSS3ID
    backgroundBucket
    backgroundColor
    sendData
    syncPlay
    externalStorage
    usedStorage
    totalStorage
    tz
    functionalLocationId
    groupId
    attributes
    meta
    utilsVersion
    utilsOnline
    securelinkStatus
    securelinkIp
    fleetId
    location {
      coordinates
      name
      place_id
      formatted_address
    }
  }
  ${DEVICE_LIST_FIELDS}
`;

// --- Queries ---

// NOTE: The DEVICES_QUERY intentionally inlines field selections instead of
// using the ...DeviceListFields fragment. The backend's device resolver uses
// extractFields(info.fieldNodes) to build MongoDB projections, but it doesn't
// resolve GraphQL fragment spreads — it reads AST selection names directly.
// Using a named fragment here would cause the backend to project only
// {_id: 1, DeviceListFields: 1} instead of the actual fields, returning
// documents with missing data. Mutations are unaffected because they return
// full documents without projection-based field selection.
export const DEVICES_QUERY = gql`
  query Devices(
    $first: Int
    $after: String
    $teamId: String
    $query: QueryDeviceInput
    $sort: DeviceSort
    $extendParams: ExtendParams
  ) {
    devices(
      first: $first
      after: $after
      teamId: $teamId
      query: $query
      sort: $sort
      extendParams: $extendParams
    ) {
      page {
        edges {
          cursor
          node {
            _id
            UUID
            accountId
            activationDate
            browser
            browserVersion
            creationDate
            manufacturer
            osVersion
            serialNo
            status
            currentAssetId
            currentAssignment
            currentPlaylistId
            currentScheduleId
            currentSelectionDate
            currentType
            device
            deviceName
            feature
            flashAssetId
            functionalLocationId
            lastHeartBeat
            lastUpdatedDate
            location {
              coordinates
              name
            }
            manufacturer
            model
            muted
            orientation
            os
            path
            platform
            recentAssignments
            scale
            tags
            teamId
            typeBuild
            deviceTime
            availableStorage
            macAddress
            serverTime
            totalMem
            utilsLastUpdateDate
            lastTeamId
            lastUpdatedBy
            addToPlaylist
            playlistData
            lastAccountId
            shareTos
            refreshRequest
            tagRule
            tagRules
            assignItem {
              key
              value
              type
              teamId
              isAccessible
            }
            flashMessage
            isContentExpired
            fleetId
            classification {
              platform
              vendor
              hardwareFamily
              managementCapability
              osVersionDisplay
              osVersionMajor
              modelCode
              manufacturerRaw
              platformRaw
            }
            availableCommands
            reportedCapabilities
            original
            preloadId
            scheduleGoLive
            statusContent
            temporarily
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
      extensions {
        groups {
          _id
          name
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
        }
      }
    }
  }
`;

// Lightweight Atlas Search query for device picker combobox.
// Returns flat array (not Relay-paginated). Only fields needed for display + eligibility.
export const SEARCH_DEVICES_PICKER = gql`
  query SearchDevicesPicker($teamId: String, $input: SearchInput!) {
    searchDevices(teamId: $teamId, input: $input) {
      _id
      deviceName
      path
      platform
      os
      model
      typeBuild
      status
      feature
      isWebViewer
      localAppVersion
      tags
    }
  }
`;

// Recent devices (≤5). Fields must cover the row plus what DevicePickerCombobox reads off a
// selected device. Must inline fields — backend extractFields doesn't resolve fragments.
export const DEVICE_RECENTS_QUERY = gql`
  query DeviceRecents($first: Int, $after: String, $sort: DataRecentSort, $teamId: String) {
    deviceRecents(first: $first, after: $after, sort: $sort, teamId: $teamId) {
      page {
        edges {
          node {
            _id
            deviceName
            path
            teamId
            os
            model
            feature
            isWebViewer
            localAppVersion
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// Batch fetch devices by IDs for realtime subscription cache sync.
// Returns Device[] (same __typename as list) so Apollo auto-merges into list cache.
// Includes assignItem + recentAssignments so content assignment changes (e.g. OptiBot push)
// update the list UI immediately without a full page refetch.
export const DEVICES_BY_IDS = gql`
  query DevicesByIds($ids: [String!]!, $teamId: String) {
    devicesByIds(ids: $ids, teamId: $teamId) {
      _id
      UUID
      deviceName
      accountId
      teamId
      path
      lastHeartBeat
      serverTime
      deviceTime
      status
      os
      device
      model
      platform
      currentType
      currentAssetId
      currentAssignment
      currentPlaylistId
      currentScheduleId
      currentSelectionDate
      scale
      orientation
      tags
      muted
      activationDate
      creationDate
      location {
        coordinates
        name
      }
      preloadId
      feature
      flashAssetId
      temporarily
      scheduleGoLive
      statusContent
      original
      flashMessage
      recentAssignments
      assignItem {
        key
        value
        type
        teamId
        isAccessible
      }
      fleetId
      lastUpdatedDate
      availableStorage
      macAddress
      totalMem
      utilsLastUpdateDate
      lastTeamId
      lastUpdatedBy
      addToPlaylist
      playlistData
      lastAccountId
      shareTos
      refreshRequest
      tagRule
      tagRules
    }
  }
`;

// Export query — uses existing `devices` relay resolver with first:99999 to fetch all.
// Pass a specific teamId to scope to one team. For "All Teams", the frontend
// iterates over each team and aggregates results (no "ALL" sentinel on this backend).
// Requests additional fields (meta, feature, pairingCode, etc.) needed for CSV export
// that DEVICES_QUERY omits to keep list page payloads lean.
// pageData.total provides counts without needing a separate count query.
export const DEVICES_FOR_EXPORT = gql`
  query DevicesForExport($teamId: String, $first: Int) {
    devices(teamId: $teamId, first: $first) {
      page {
        edges {
          node {
            _id
            UUID
            deviceName
            pairingCode
            status
            statusContent
            currentType
            osVersion
            manufacturer
            serialNo
            localAppVersion
            isWebViewer
            usedStorage
            totalStorage
            functionalLocationId
            orientation
            location {
              name
            }
            tz
            lastHeartBeat
            creationDate
            currentSelectionDate
            lastUpdatedDate
            tags
            path
            teamId
            feature
            meta
            assignItem {
              key
              value
              type
              teamId
              isAccessible
            }
          }
        }
      }
      pageData {
        total
      }
    }
  }
`;

export const DEVICE_TAGS_QUERY = gql`
  query DeviceTags($teamId: String, $query: QueryDeviceInput, $extendParams: ExtendParams) {
    deviceTags(teamId: $teamId, query: $query, extendParams: $extendParams)
  }
`;

// NOTE: Inlined for the same reason as DEVICES_QUERY — the getDeviceDetail
// resolver uses extractFields() to conditionally resolve assignItem,
// scheduleGoLive, temporarily, and recentAssignments. Fragment spreads would
// cause these checks to fail, returning unresolved fields.
export const GET_DEVICE_DETAIL_QUERY = gql`
  query GetDeviceDetail($id: String!, $teamId: String) {
    getDeviceDetail(_id: $id, teamId: $teamId) {
      _id
      UUID
      accountId
      activationDate
      attributes
      backgroundColor
      backgroundType
      browser
      browserVersion
      creationDate
      currentAssetId
      currentAssignment
      currentPlaylistId
      currentScheduleId
      currentSelectionDate
      currentType
      device
      deviceName
      documentDuration
      externalStorage
      feature
      flashAssetId
      functionalLocationId
      heartbeatInterval
      isVirtual
      isWebViewer
      lastHeartBeat
      liveLastHeartBeat
      lastUpdatedDate
      localAppVersion
      location {
        coordinates
        name
        place_id
        formatted_address
      }
      manufacturer
      model
      muted
      orientation
      os
      osVersion
      pairingCode
      path
      platform
      pollingInterval
      recentAssignments
      scale
      sendData
      serialNo
      syncPlay
      tags
      teamId
      typeBuild
      tz
      usedStorage
      utilsOnline
      utilsVersion
      videoPlayerType
      deviceTime
      availableStorage
      macAddress
      serverTime
      totalMem
      utilsLastUpdateDate
      lastTeamId
      lastUpdatedBy
      addToPlaylist
      playlistData
      lastAccountId
      groupId
      stretchAsset
      shareTos
      refreshRequest
      tagRule
      tagRules
      assignItem {
        key
        value
        type
        teamId
        isAccessible
      }
      backgroundAWSS3ID
      backgroundAssetId
      backgroundBucket
      flashMessage
      isContentExpired
      meta
      utilsVersion
      utilsOnline
      securelinkStatus
      securelinkIp
      takeScreens
      pairingCode
      fleetId
      classification {
        platform
        vendor
        hardwareFamily
        managementCapability
        osVersionDisplay
        osVersionMajor
        modelCode
        manufacturerRaw
        platformRaw
      }
      availableCommands
      reportedCapabilities
      hdmiActive
      original
      preloadId
      scheduleGoLive
      status
      statusContent
      temporarily
      totalStorage
      enterpriseWifiStatus
      enterpriseWifiStatusUpdatedAt
      enterpriseWifiInfoId
      enterpriseWifiCapability
    }
  }
`;

// Isolated progressive-enhancement query for the Device Info dialog — fetches
// browser/browserVersion only when the dialog opens. Called with
// errorPolicy:'all' at the consumer so a schema mismatch (backend without the
// Device.browser/browserVersion @Field additions) returns null cleanly instead
// of crashing the dialog or the parent Screens page. Keeps the main device
// queries stable across backend deploy transitions.
export const DEVICE_BROWSER_INFO_QUERY = gql`
  query DeviceBrowserInfo($id: String!, $teamId: String) {
    getDeviceDetail(_id: $id, teamId: $teamId) {
      _id
      browser
      browserVersion
    }
  }
`;

// --- Mutations ---

// NOTE: pairDevice and updateDevice return DeviceDetailResponse (not Device),
// so we inline fields instead of using ...DeviceListFields fragment (which is
// defined on Device). GraphQL rejects fragment spreads on mismatched types.
export const PAIR_DEVICE = gql`
  mutation PairDevice($teamId: String, $payload: PairDeviceInput!) {
    pairDevice(teamId: $teamId, payload: $payload) {
      _id
      UUID
      deviceName
      accountId
      teamId
      path
      lastHeartBeat
      serverTime
      deviceTime
      status
      os
      device
      model
      platform
      currentType
      currentAssetId
      currentPlaylistId
      currentScheduleId
      currentSelectionDate
      scale
      orientation
      tags
      muted
      activationDate
      creationDate
      location {
        coordinates
        name
      }
      feature
      temporarily
      scheduleGoLive
      flashMessage
      recentAssignments
      assignItem {
        key
        value
        type
        teamId
        isAccessible
      }
    }
  }
`;

export const UPDATE_DEVICE = gql`
  mutation UpdateDevice($id: String!, $teamId: String, $payload: UpdateDeviceInput!) {
    updateDevice(_id: $id, teamId: $teamId, payload: $payload) {
      _id
      UUID
      deviceName
      accountId
      teamId
      path
      lastHeartBeat
      serverTime
      deviceTime
      status
      os
      device
      model
      platform
      currentType
      currentAssetId
      currentPlaylistId
      currentScheduleId
      currentSelectionDate
      lastUpdatedDate
      scale
      orientation
      tags
      muted
      activationDate
      creationDate
      location {
        coordinates
        name
        formatted_address
      }
      feature
      temporarily
      scheduleGoLive
      flashMessage
      recentAssignments
      assignItem {
        key
        value
        type
        teamId
        isAccessible
      }
      attributes
      backgroundType
      backgroundColor
      backgroundAssetId
      backgroundAWSS3ID
      backgroundBucket
      syncPlay
      videoPlayerType
      externalStorage
      pollingInterval
      heartbeatInterval
      documentDuration
      functionalLocationId
      fleetId
    }
  }
`;

export const PUSH_TO_SCREENS = gql`
  mutation PushToScreens($teamId: String, $payload: PushToScreensInput!, $force: Boolean) {
    pushToScreens(teamId: $teamId, payload: $payload, force: $force)
  }
`;

export const UNPAIR_DEVICES = gql`
  mutation UnPairDevices($teamId: String, $payload: UnPairDeviceInput!) {
    unPairDevices(teamId: $teamId, payload: $payload)
  }
`;

// Separate query for device folder groups — fetches folders at a specific path
// for folder tree navigation (e.g., MoveToFolderDialog). Includes security
// fields needed for folder-level permission filtering.
export const DEVICE_GROUPS_QUERY = gql`
  query DeviceGroups(
    $query: QueryGroupInput
    $first: Int
    $after: String
    $sort: GroupSort
    $teamId: String
    $extendParams: ExtendParams
  ) {
    groups(
      query: $query
      first: $first
      after: $after
      sort: $sort
      teamId: $teamId
      extendParams: $extendParams
    ) {
      page {
        edges {
          cursor
          node {
            _id
            name
            type
            path
            teamId
            accountId
            lastUpdatedDate
            createdAt
            isHoldDevice
            isFolderSecurityEdit
            isFolderSecurityView
            securities {
              userId
              role
            }
            securityTeams
            securityTeamsDetail {
              name
              _id
            }
            securitiesDetail {
              name
              userId
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// Toggle a favorite (star) for the current user + team. Idempotent server-side.
// `type` is the RECENT_TYPES GraphQL enum — send UPPERCASE keys ('DEVICE', 'ASSET',
// 'PLAYLIST', 'SCHEDULE'); the backend stores the lowercase value. Returns Boolean;
// the UI updates the cache optimistically (the mutation does not return the entity).
export const TOGGLE_FAVORITE = gql`
  mutation ToggleFavorite($payload: ToggleFavoriteInput!) {
    toggleFavorite(payload: $payload)
  }
`;

// Separate lightweight query for folder device counts.
// Fires after folders load so the folder list renders fast.
// Security-aware: only counts devices in sub-folders the user can access.
export const GROUP_DEVICE_COUNTS_QUERY = gql`
  query GroupDeviceCounts($teamId: String, $groupIds: [String!]!) {
    groupDeviceCounts(teamId: $teamId, groupIds: $groupIds) {
      groupId
      count
    }
  }
`;

export const EDIT_DEVICE_GROUPS = gql`
  mutation EditDeviceGroups($teamId: String, $payload: UpdateDevicesInput!, $force: Boolean) {
    editDeviceGroups(teamId: $teamId, payload: $payload, force: $force)
  }
`;

export const MOVE_DEVICES_TO_TEAM = gql`
  mutation MoveDevicesToTeam($teamId: String, $payload: MoveToTeamInput!) {
    moveDevicesToTeam(teamId: $teamId, payload: $payload)
  }
`;

export const IDENTIFY_SCREEN = gql`
  mutation IdentifyScreen($teamId: String, $payload: IdentifyScreenInput!) {
    identifyScreen(teamId: $teamId, payload: $payload)
  }
`;

export const CANCEL_SCHEDULE_GO_LIVE = gql`
  mutation CancelScheduleGoLive($teamId: String, $payload: DeviceIdsInput!) {
    doCancelScheduleGoLive(teamId: $teamId, payload: $payload)
  }
`;

export const CANCEL_TEMPORARILY = gql`
  mutation CancelTemporarily($teamId: String, $payload: DeviceIdsInput!) {
    doCancelTemporarily(teamId: $teamId, payload: $payload)
  }
`;

// --- Additional Queries ---

export const DEVICE_TAG_RULES_QUERY = gql`
  query DeviceTagRules(
    $teamId: String
    $after: String
    $first: Int
    $query: QueryDeviceTagRuleInput!
    $sort: DeviceTagRuleSort
    $extendParams: ExtendParams
  ) {
    deviceTagRules(
      teamId: $teamId
      after: $after
      first: $first
      query: $query
      sort: $sort
      extendParams: $extendParams
    ) {
      page {
        edges {
          cursor
          node {
            _id
            name
            teamId
            currentType
            inclusionTags
            exclusionTags
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
      pageData {
        limit
        offset
      }
    }
  }
`;

// NOTE: getDeviceHeartBeatByUUIDs returns KeyValueItem[] where:
// - key: device UUID
// - value: heartbeat timestamp (as string/number)
// - type: optional type indicator
export const DEVICE_HEARTBEATS_QUERY = gql`
  query DeviceHeartbeats($teamId: String, $uuids: [String!]!) {
    getDeviceHeartBeatByUUIDs(teamId: $teamId, uuids: $uuids) {
      key
      value
      type
      teamId
      isAccessible
    }
  }
`;

// --- Subscriptions ---
// NOTE: Subscription operations moved to shared subscriptions.ts.
// The backend exposes a single `subscribe(teamId)` resolver that returns
// { type, _id, mutation } for all entity types — not per-entity subscriptions.

// --- Additional Queries ---

export const GET_REMOTE_CONTROL_LINK = gql`
  query GetRemoteControlLink($id: String!, $teamId: String) {
    getRemoteControlLink(_id: $id, teamId: $teamId)
  }
`;

export const DEVICE_DATA_SPECIAL = gql`
  query DeviceDataSpecial($id: String!, $teamId: String) {
    deviceDataSpecial(_id: $id, teamId: $teamId)
  }
`;

// --- Additional Mutations ---

export const EXECUTE_REMOTE_COMMAND = gql`
  mutation ExecuteRemoteCommand($teamId: String, $payload: ExecuteRemoteCommandInput!) {
    executeRemoteCommand(teamId: $teamId, payload: $payload)
  }
`;

export const SAVE_DEVICE_DATA_SPECIAL = gql`
  mutation SaveDeviceDataSpecial($teamId: String, $payload: DeviceDataSpecialInput!) {
    saveDeviceDataSpecial(teamId: $teamId, payload: $payload)
  }
`;

// Toggle a device feature (ai, sensor, remoteControl, backgroundMusic, lockdown,
// playbackControl, videoWall). Used by the Edit Screen dialog when toggling add-on
// features that require plan/license validation separate from the main save flow.
// Backend validates addon licenses and plan tier before toggling.
export const TOGGLE_DEVICE_FEATURE = gql`
  mutation ToggleDeviceFeature($teamId: String, $payload: ToggleDeviceFeatureInput!) {
    toggleDeviceFeature(teamId: $teamId, payload: $payload)
  }
`;

// Bulk assign an add-on (Background Music, AeriCast, Video Wall) to multiple devices.
// Backend validates addon licenses, handles trial/paid subscription checks, and
// sets device feature flags + content assignment. Returns {screens: {status: true}}.
export const ADD_WIDGET_ADDON_DEVICES = gql`
  mutation AddWidgetAddonDevices($teamId: String, $payload: AddWidgetAddonDevicesInput!) {
    addWidgetAddonDevices(teamId: $teamId, payload: $payload)
  }
`;

export const PRE_CONFIGURE_DEVICES = gql`
  mutation PreConfigureDevices($teamId: String, $payload: PreConfigureDevicesInput!) {
    preConfigureDevices(teamId: $teamId, payload: $payload)
  }
`;

// BG4-revived: 7-day uptime sparkline. Batched query — pass all visible
// row deviceIds in one call so the table doesn't fire N queries. Server
// returns one outer row per requested deviceId; `points` array length
// always === `days` (server fills missing days with zero-sample sentinels
// so the frontend Sparkline doesn't branch).
//
// Spec: claude-dev/bugfix-alan/2026-05-26-mdm-redesign/03-frontend-redesign-plan.md §A
export const DEVICES_UPTIME = gql`
  query DevicesUptime($deviceIds: [String!]!, $days: Int) {
    devicesUptime(deviceIds: $deviceIds, days: $days) {
      deviceId
      points {
        day
        samples
        online
      }
    }
  }
`;
